import { useEffect, useRef } from 'react';

import { writeApiRequestSourceStorage } from '@utils/cresta/crestaLeadStorage';

/**
 * GA4-compatible context object containing page and session metadata.
 * Required fields are used for GA4 event enrichment.
 * Additional properties can be included for tracking custom dimensions.
 */
export interface CrestaPageContext {
	/**
	 * Brand identifier (e.g., 'ClearChoice', 'Aspen Dental').
	 */
	brand: string;
	/**
	 * Platform name (e.g., 'NextJS', 'React').
	 */
	platform: string;
	/**
	 * User-readable page or content name.
	 */
	page_origin_name: string;
	/**
	 * Full page URL or origin.
	 */
	page_origin_url: string;
	/**
	 * Unique session identifier for grouping events.
	 */
	tag_session_id?: string;
	/**
	 * Allow additional custom properties for brand-specific tracking.
	 * These will be passed through to GA4 as-is.
	 */
	[key: string]: unknown;
}

/**
 * Cresta event names that trigger GA4 tracking.
 * These are dispatched from the Cresta widget as custom window events.
 */
type CrestaEventName =
	| 'CRESTA_CHAT_STARTED'
	| 'CRESTA_CHAT_VISITOR_MESSAGE_SENT'
	| 'CRESTA_AGENT_MESSAGE_SENT'
	| 'CRESTA_CHAT_ENDED'
	| 'CRESTA_CHAT_ERROR';

/**
 * Maps Cresta event names to standardized GA4 event names.
 * Used for consistent event naming in Google Analytics 4.
 */
const CRESTA_GA_EVENT_MAP: Record<CrestaEventName, string> = {
	CRESTA_CHAT_STARTED: 'chatbot_chat_started',
	CRESTA_CHAT_VISITOR_MESSAGE_SENT: 'chatbot_visitor_message_sent',
	CRESTA_AGENT_MESSAGE_SENT: 'chatbot_agent_message_sent',
	CRESTA_CHAT_ENDED: 'chatbot_chat_ended',
	CRESTA_CHAT_ERROR: 'chatbot_chat_error',
};

/**
 * Subscribes to Cresta chat widget window events and handles two primary concerns:
 *
 * **1. GA4 Event Tracking:**
 * Listens for Cresta chat events and pushes enriched events to `globalThis.window.dataLayer`
 *
 * **2. Lead Attribution (Session Storage):**
 * When a visitor sends a message (`CRESTA_CHAT_VISITOR_MESSAGE_SENT`), writes
 * a flag to session storage so downstream apps can attribute the lead to the
 * Cresta chat interaction. Session storage is automatically cleared when the
 * browser tab closes, ensuring attribution doesn't carry to return sessions.
 *
 * @param enabled - When true, registers Cresta event listeners. When false, does nothing.
 * @param resolvePayload - Optional function that returns the current GA4 context.
 *                        Called at event dispatch time to ensure latest values.
 *                        If not provided, events will not be pushed to dataLayer.
 */
export function useCrestaEventManager(enabled: boolean, resolvePayload?: () => CrestaPageContext): void {
	// Ref keeps the resolver up to date on every render without re-registering
	// event listeners, which would happen if resolvePayload were a useEffect dep.
	const resolvePayloadRef = useRef(resolvePayload);
	resolvePayloadRef.current = resolvePayload;

	useEffect(() => {
		if (!enabled) return;

		// Guard: only available in browser environment
		if (globalThis.window === undefined) return;
		const win = globalThis.window as Window & {
			__crestaEventManagerRegistered?: boolean;
			dataLayer?: Record<string, unknown>[];
		};

		// Guard: only one set of listeners per page load
		if (win.__crestaEventManagerRegistered) return;
		win.__crestaEventManagerRegistered = true;

		let storageWritten = false;

		/**
		 * @param payload - GA4 event object with event name and context properties
		 */
		function pushToDataLayer(payload: Record<string, unknown>): void {
			win.dataLayer ??= [];
			win.dataLayer.push(payload);
		}

		/**
		 * @param crestaEventName - The Cresta event type (e.g., 'CRESTA_CHAT_STARTED')
		 * @returns Event handler function
		 */
		function handleCrestaEvent(crestaEventName: CrestaEventName) {
			return (): void => {
				if (crestaEventName === 'CRESTA_CHAT_VISITOR_MESSAGE_SENT' && !storageWritten) {
					storageWritten = true;
					writeApiRequestSourceStorage();
				}
				try {
					// Only push to dataLayer if resolvePayload is provided
					const resolver = resolvePayloadRef.current;
					if (!resolver) return;

					// Resolve the latest context at event time
					const payload = resolver();

					// Push enriched GA4 event to dataLayer
					pushToDataLayer({
						event: CRESTA_GA_EVENT_MAP[crestaEventName],
						...payload,
					});
				} catch (error) {
					// Log error but don't crash the entire handler
					console.warn('[Cresta Event Manager] Failed to process event:', {
						crestaEventName,
						error,
					});
				}
			};
		}

		// Register listeners for all Cresta event types
		const listeners = (Object.keys(CRESTA_GA_EVENT_MAP) as CrestaEventName[]).map((crestaEventName) => {
			const handler = handleCrestaEvent(crestaEventName);
			win.addEventListener(crestaEventName, handler);
			return { crestaEventName, handler };
		});

		// Cleanup: Remove all listeners on unmount or when hook is disabled
		return () => {
			listeners.forEach(({ crestaEventName, handler }) => {
				win.removeEventListener(crestaEventName, handler);
			});
			win.__crestaEventManagerRegistered = false;
		};
	}, [enabled]);
}
